import { logPrint } from "./my-utils";

/**
 * 浏览器原生文件上传服务
 * 替代 Electron 主进程的文件上传实现
 */
export class FileUploadUtils {
  /**
   * 上传文件到指定 URL（浏览器原生实现）
   * @param uploadUrl 上传 URL
   * @param file 文件对象
   * @param onProgress 上传进度回调
   * @returns Promise<{success: boolean, error?: string}>
   */
  static async uploadFile(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{success: boolean, error?: string}> {
    try {
      logPrint('开始上传文件（浏览器原生方式）:', { fileName: file.name, size: file.size, uploadUrl });

      onProgress?.(0);

      // 使用 fetch API 上传文件（匹配 Flutter 的 client.put 逻辑）
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
          // 重要：不设置 Authorization 头，匹配 Flutter 的 options.headers!.remove('Authorization')
        }
      });

      // 模拟进度更新（fetch API 无法获取真实上传进度）
      onProgress?.(100);

      // 检查响应状态（匹配 Flutter 的 response.statusCode == 200）
      if (response.ok) {
        logPrint('文件上传成功！状态码:', response.status);
        return { success: true };
      } else {
        const errorMsg = `上传失败: ${response.status} ${response.statusText}`;
        console.error(errorMsg);
        return { success: false, error: errorMsg };
      }

    } catch (error: any) {
      console.error('文件上传失败:', error);
      return { success: false, error: error.message || '上传失败' };
    }
  }

  /**
   * 使用 XMLHttpRequest 上传文件（支持真实进度监控）
   * @param uploadUrl 上传 URL
   * @param file 文件对象
   * @param onProgress 上传进度回调
   * @returns Promise<{success: boolean, error?: string}>
   */
  static async uploadFileWithProgress(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{success: boolean, error?: string}> {
    return new Promise((resolve) => {
      try {
        logPrint('开始上传文件（XMLHttpRequest方式）:', { fileName: file.name, size: file.size, uploadUrl });

        const xhr = new XMLHttpRequest();

        // 监听上传进度
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            onProgress?.(percentCompleted);
          }
        });

        // 监听请求完成
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            logPrint('文件上传成功！状态码:', xhr.status);
            resolve({ success: true });
          } else {
            const errorMsg = `上传失败: ${xhr.status} ${xhr.statusText}`;
            console.error(errorMsg);
            resolve({ success: false, error: errorMsg });
          }
        });

        // 监听请求错误
        xhr.addEventListener('error', () => {
          console.error('文件上传网络错误');
          resolve({ success: false, error: '网络错误' });
        });

        // 监听请求超时
        xhr.addEventListener('timeout', () => {
          console.error('文件上传超时');
          resolve({ success: false, error: '上传超时' });
        });

        // 配置请求
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.timeout = 300000; // 5分钟超时

        // 发送文件
        xhr.send(file);

      } catch (error: any) {
        console.error('文件上传失败:', error);
        resolve({ success: false, error: error.message || '上传失败' });
      }
    });
  }

  /**
   * 批量上传文件
   * @param uploads 上传任务列表
   * @param onProgress 整体进度回调
   * @returns Promise<{success: boolean, results: any[], error?: string}>
   */
  static async uploadMultipleFiles(
    uploads: Array<{
      uploadUrl: string;
      file: File;
    }>,
    onProgress?: (completed: number, total: number) => void
  ): Promise<{success: boolean, results: any[], error?: string}> {
    const results: any[] = [];
    let completed = 0;

    try {
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        logPrint(`开始上传第 ${i + 1}/${uploads.length} 个文件:`, upload.file.name);

        const result = await this.uploadFileWithProgress(
          upload.uploadUrl,
          upload.file
        );

        results.push({
          index: i,
          fileName: upload.file.name,
          ...result
        });

        completed++;
        onProgress?.(completed, uploads.length);

        logPrint(`第 ${i + 1} 个文件上传完成:`, result);
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      logPrint(`批量上传完成: 成功 ${successCount}，失败 ${failCount}`);

      return {
        success: failCount === 0,
        results,
        error: failCount > 0 ? `${failCount} 个文件上传失败` : undefined
      };

    } catch (error: any) {
      console.error('批量上传错误:', error);
      return {
        success: false,
        results,
        error: error.message
      };
    }
  }

  /**
   * 读取文件内容为 Base64（用于预览）
   * @param file 文件对象
   * @returns Promise<string | null> Base64 数据 URL
   */
  static async readFileAsBase64(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        
        reader.onload = (event) => {
          if (event.target?.result) {
            resolve(event.target.result as string);
          } else {
            resolve(null);
          }
        };
        
        reader.onerror = () => {
          console.error('读取文件失败');
          resolve(null);
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('读取文件失败:', error);
        resolve(null);
      }
    });
  }

  /**
   * 检查文件类型是否被支持
   * @param file 文件对象
   * @param allowedTypes 允许的文件类型列表
   * @returns boolean
   */
  static isFileTypeAllowed(file: File, allowedTypes: string[]): boolean {
    if (allowedTypes.includes('*')) return true;
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    return allowedTypes.some(type => 
      type === fileExtension || 
      mimeType.startsWith(type.replace('*', ''))
    );
  }

  /**
   * 格式化文件大小
   * @param bytes 文件大小（字节）
   * @returns 格式化后的文件大小字符串
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 验证文件大小
   * @param file 文件对象
   * @param maxSizeInMB 最大文件大小（MB）
   * @returns boolean
   */
  static isFileSizeValid(file: File, maxSizeInMB: number): boolean {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  }

  /**
   * 创建文件选择器
   * @param options 选择器选项
   * @returns Promise<File[]> 选择的文件列表
   */
  static async selectFiles(options: {
    multiple?: boolean;
    accept?: string;
    title?: string;
  } = {}): Promise<File[]> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = options.multiple ?? false;
      input.accept = options.accept ?? '*';
      
      input.onchange = (event) => {
        const target = event.target as HTMLInputElement;
        if (target.files) {
          resolve(Array.from(target.files));
        } else {
          resolve([]);
        }
      };
      
      input.oncancel = () => {
        resolve([]);
      };
      
      input.click();
    });
  }
}
